import { Node } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';

export { TableRow } from '@tiptap/extension-table-row';

// Canonical inline table styles — kept 1:1 with the backend's system tables
// (e.g. `installments-table.hbs`) so editor output renders identically across
// every sink: web preview, PDF, and mail.
//
// WHY INLINE `style` AND NOT A CLASS OR STYLESHEET
// TipTap keeps its table grid only in the editor's own CSS (`richTextEditor.css`),
// which never travels with the saved HTML. The backend `HtmlSanitizer` is
// deny-by-default: it strips `class` (outside mail) and all `<style>` blocks,
// and the PDF renderer ships no editor CSS at all. The only styling that
// survives sanitization and reaches every sink is an inline `style` attribute on
// each element (OWASP-validated property by property). So the borders must be
// serialized onto the elements themselves.
//
// HOW IT STAYS IDEMPOTENT AND ROUND-TRIP STABLE
// These strings are fed through each extension's `HTMLAttributes` option, which
// the base `renderHTML` merges into the element via `mergeAttributes`. `style`
// is NOT a schema attribute on these nodes, so `parseHTML` drops any inline
// style on load and `renderHTML` re-derives it deterministically from the
// static config. Re-saving therefore never duplicates or appends another
// `border` (open → save → identical output). `colspan`/`rowspan` pass through
// untouched — they already survive the sanitizer.
//
// Legacy presentational attributes (`border`, `cellpadding`, `bgcolor`,
// `width`, `colwidth`) are intentionally NOT used: the sanitizer cuts them.
//
// WHY `width: 100%` AND `table-layout: fixed`
// A table that was never column-resized carries only `min-width` (TipTap emits
// `min-width` on the table and each `<col>`, never `width`). The `.cd-doc` base
// CSS now reaches every sink (editor, preview, PDF) and forces
// `table { width: 100%; table-layout: fixed }`, but we still pin both inline:
// for a RESIZED table the base `renderHTML` emits a computed `width: NNNpx`,
// and only an inline value outranks the base stylesheet's `100%`. `width: 100%`
// makes the table fill the page; `table-layout: fixed` makes the `<col>` widths
// authoritative, so a narrowed column stays narrow instead of stretching to fit.
const TABLE_STYLE =
  'border-collapse: collapse; width: 100%; table-layout: fixed';
const TABLE_HEADER_STYLE =
  'border: 1px solid #888; padding: 6px 8px; text-align: left; background-color: #f3f3f3';
const TABLE_CELL_STYLE = 'border: 1px solid #888; padding: 6px 8px';

// Editor-only reference canvas. Persisted column widths are PERCENTAGES (see
// `tiptapToTemplate.normalizeTableColumnWidths`), but TipTap's model is px-based,
// so on load we map each `%` onto this px width. It only sets how wide a
// freshly-loaded table looks in the editor before the user touches it — the
// proportions are exact and the wire format stays `%`, so this constant never
// reaches the PDF.
const REFERENCE_TABLE_WIDTH = 700;

// Read one `<col>`'s declared width into px for the editor model. Percentages
// (the persisted format) are scaled onto the reference canvas; a px value (in
// an in-session/non-sanitized table) is taken verbatim; anything else (e.g. a
// never-resized `min-width`) is 0.
const parseColWidthToPx = (rawWidth: string | undefined): number => {
  if (!rawWidth) return 0;
  if (rawWidth.endsWith('%')) {
    return (parseFloat(rawWidth) / 100) * REFERENCE_TABLE_WIDTH;
  }
  const px = parseInt(rawWidth, 10);
  return Number.isFinite(px) ? px : 0;
};

// Recover a cell's `colwidth` from the table's `<colgroup>`.
//
// TipTap persists column widths twice: as `colwidth` on each cell AND as
// `<col style="width:…">`. The backend sanitizer strips the `colwidth` attribute
// (legacy), leaving the width only in the surviving `<col>` style. The stock
// parser cannot read it back — it looks at the col's `width` *attribute* (we
// only ever emit a `style`) and additionally skips the first column
// (`if (cellIndex && ...)` is falsy for index 0). So every column resize was
// silently lost on save → reload. This reads the width from the matching
// `<col>`'s inline `style` instead, for every column.
const recoverColwidth = (element: HTMLElement): number[] | null => {
  // In-session / non-sanitized HTML still has the attribute — trust it first.
  const own = element.getAttribute('colwidth');
  if (own) return own.split(',').map((width) => parseInt(width, 10));

  const cols = Array.from(
    element.closest('table')?.querySelectorAll<HTMLElement>('colgroup > col') ??
      [],
  );
  if (cols.length === 0) return null;

  const siblings = Array.from(element.parentElement?.children ?? []);
  const cellIndex = siblings.indexOf(element);
  if (cellIndex < 0) return null;

  // The cell's first column = sum of preceding cells' colspans in this row.
  let colStart = 0;
  for (let i = 0; i < cellIndex; i += 1) {
    colStart += parseInt(siblings[i].getAttribute('colspan') ?? '1', 10) || 1;
  }
  const colspan = parseInt(element.getAttribute('colspan') ?? '1', 10) || 1;

  const widths: number[] = [];
  for (let i = 0; i < colspan; i += 1) {
    widths.push(parseColWidthToPx(cols[colStart + i]?.style.width));
  }
  return widths.some((width) => width > 0) ? widths : null;
};

// Re-point the `colwidth` attribute's `parseHTML` at `recoverColwidth` while
// leaving every other attribute (colspan/rowspan) and the node's render path
// untouched.
const withColwidthRecovery = <T extends Node>(extension: T): T =>
  extension.extend({
    addAttributes() {
      const parent = this.parent?.() ?? {};
      return {
        ...parent,
        colwidth: {
          ...parent.colwidth,
          parseHTML: recoverColwidth,
        },
      };
    },
  }) as T;

export const StyledTable = Table.configure({
  resizable: true,
  HTMLAttributes: { style: TABLE_STYLE },
});

export const StyledTableHeader = withColwidthRecovery(TableHeader).configure({
  HTMLAttributes: { style: TABLE_HEADER_STYLE },
});

export const StyledTableCell = withColwidthRecovery(TableCell).configure({
  HTMLAttributes: { style: TABLE_CELL_STYLE },
});
